import React from 'react';
import Type from 'prop-types';
import { useRifm } from 'rifm';
import { TextField } from '@mui/material';
import { isNumber } from 'lodash';
import { NUMBER_REGEX } from 'constants/regexp';
import { parseNumber } from 'utils/number';
import { getCurrencySymbol } from 'utils/string';

const CurrencyInput = (props) => {
  const {
    acceptRegex,
    formatter,
    value: valueProp,
    onChange,
    onChangeEvent,
    onFocus,
    onBlur,
    name,
    showCurrency,
    inputProps,
    options,
    ...other
  } = props;
  const [isFocused, setIsFocused] = React.useState(false);
  const value = isNumber(valueProp) ? valueProp.toString() : valueProp;

  const formatCurrency = (text) => {
    const { currency, maximumFractionDigits } = options;
    const maxDigits = maximumFractionDigits || 2;
    const currencySymbol = getCurrencySymbol(currency);
    const parsedNumber = parseNumber(text);

    if (!parsedNumber || parsedNumber === '') {
      return '';
    }

    const number = Number.parseFloat(parsedNumber);

    if (Number.isNaN(number)) {
      return '';
    }

    const amount = number.toLocaleString('ru-RU', {
      minimumFractionDigits: 0,
      maximumFractionDigits: maxDigits,
    });

    return showCurrency && !isFocused ? `${amount} ${currencySymbol}` : amount;
  };

  const handleFocus = (event) => {
    setIsFocused(true);
    event.target.select();

    if (onFocus) {
      onFocus(event);
    }
  };

  const handleBlur = (event) => {
    setIsFocused(false);

    if (onBlur) {
      onBlur(event);
    }
  };

  const handleChange = (text) => {
    const rawValue = parseNumber(text);

    if (onChangeEvent) {
      onChangeEvent({ target: { name, value: rawValue } });
      return;
    }

    if (onChange) {
      onChange(rawValue);
    }
  };

  const rifm = useRifm({
    value,
    accept: acceptRegex,
    format: formatter || formatCurrency,
    onChange: handleChange,
  });

  return (
    <TextField
      type="tel"
      name={name}
      value={rifm.value}
      inputProps={{
        autoComplete: 'off',
        inputMode: 'decimal',
        pattern: '[0-9]*',
        ...inputProps,
      }}
      onChange={rifm.onChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      {...other}
    />
  );
};

CurrencyInput.propTypes = {
  value: Type.oneOfType([Type.string, Type.number]).isRequired,
  onChange: Type.func,
  onChangeEvent: Type.func,
  onFocus: Type.func,
  onBlur: Type.func,
  name: Type.string,
  id: Type.string,
  showCurrency: Type.bool,
  acceptRegex: Type.instanceOf(RegExp),
  formatter: Type.func,
  options: Type.shape({
    currency: Type.string.isRequired,
    maximumFractionDigits: Type.number,
  }),
  inputProps: Type.shape({
    autoComplete: Type.string,
    inputMode: Type.oneOf(['numeric', 'decimal']),
    pattern: Type.string,
  }),
  // eslint-disable-next-line react/forbid-prop-types
  sx: Type.any,
  variant: Type.oneOf(['standard', 'outlined', 'filled']),
  placeholder: Type.string,
  error: Type.bool,
  helperText: Type.node,
  label: Type.node,
  fullWidth: Type.bool,
  disabled: Type.bool,
  size: Type.oneOf(['small', 'medium']),
};

CurrencyInput.defaultProps = {
  onChange: undefined,
  onChangeEvent: undefined,
  onFocus: undefined,
  onBlur: undefined,
  name: undefined,
  id: undefined,
  showCurrency: true,
  acceptRegex: NUMBER_REGEX,
  formatter: undefined,
  options: {},
  inputProps: {},
  sx: undefined,
  variant: undefined,
  placeholder: undefined,
  error: undefined,
  helperText: undefined,
  label: undefined,
  fullWidth: undefined,
  disabled: undefined,
  size: undefined,
};

export default CurrencyInput;
